"use client";
const STORAGE_KEY = 'ls_walletconnect_dynamic_metadata';
// In-memory store for metadata captured BEFORE the wallet address is known
// (e.g. between user click and the WC session completing). Keyed by namespace.
const pendingStore = new Map();
// Cache the parsed localStorage payload so repeated reads from render paths
// (useMemo in useEVM / useSVM) don't hit localStorage synchronously every time.
let _storeCache = null;
const readStore = () => {
    if (_storeCache !== null)
        return _storeCache;
    if (typeof window === 'undefined')
        return {};
    ensureStorageListener();
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        _storeCache = stored ? JSON.parse(stored) : {};
    }
    catch {
        _storeCache = {};
    }
    return _storeCache;
};
const writeStore = (store) => {
    if (typeof window === 'undefined')
        return;
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
        _storeCache = store;
    }
    catch {
        // ignore quota / serialization errors
    }
};
// Cross-tab invalidation: another tab writing to our key must bust this tab's cache.
// Lazily registered on first read/write so we never touch `window` at import time (SSR-safe).
let _storageListenerRegistered = false;
const ensureStorageListener = () => {
    if (_storageListenerRegistered || typeof window === 'undefined')
        return;
    _storageListenerRegistered = true;
    window.addEventListener('storage', (e) => {
        if (e.key === STORAGE_KEY || e.key === null)
            _storeCache = null;
    });
};
const normalizeAddress = (namespace, address) => {
    // EVM addresses are case-insensitive; Solana base58 keys are case-sensitive.
    return namespace === 'eip155' ? address.toLowerCase() : address;
};
export const getDynamicWcMetadata = (namespace, address) => {
    const store = readStore();
    const key = normalizeAddress(namespace, address);
    return store[namespace]?.[key] ?? null;
};
export const setDynamicWcMetadata = (namespace, address, meta) => {
    const store = readStore();
    const key = normalizeAddress(namespace, address);
    const ns = store[namespace] ?? {};
    ns[key] = meta;
    store[namespace] = ns;
    writeStore(store);
};
export const setPendingDynamicWcMetadata = (namespace, meta) => {
    if (meta === null) {
        pendingStore.delete(namespace);
    }
    else {
        pendingStore.set(namespace, meta);
    }
};
export const getPendingDynamicWcMetadata = (namespace) => {
    return pendingStore.get(namespace) ?? null;
};
export const clearPendingDynamicWcMetadata = (namespace) => {
    pendingStore.delete(namespace);
};
/**
 * Convenience helper used by both EVM and Solana connect flows to set/clear
 * pending metadata from a registry entry in a single call.
 */
export const setPendingMetadataForRegistry = (namespace, registry) => {
    if (!registry) {
        clearPendingDynamicWcMetadata(namespace);
        return undefined;
    }
    const meta = {
        name: registry.name,
        icon: registry.icon || '',
        id: registry.id,
        deepLink: registry.deepLink || registry.mobile?.native || registry.mobile?.universal || undefined,
    };
    setPendingDynamicWcMetadata(namespace, meta);
    return meta;
};
